import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ToastModule } from 'primeng/toast';
import SignaturePad from 'signature_pad';

import { QuotesService } from '../../services/quotes/quotes.service';

@Component({
  selector: 'app-accept-quote',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    ToastModule,
    InputTextModule,
    InputTextareaModule,
  ],
  providers: [MessageService],
  templateUrl: './accept-quote.component.html',
  styleUrl: './accept-quote.component.scss'
})
export class AcceptQuoteComponent implements OnInit {
  private _route = inject(ActivatedRoute);
  private _quotesService = inject(QuotesService);

  public quoteId: string | null = null;
  public quoteApproved = false;

  @ViewChild('digital_sign', { static: false }) digitalSign!: ElementRef;
  @ViewChild('wrapper', { static: false }) wrapper!: ElementRef;

  Form: FormGroup;
  CanvasWidth = 500;
  CanvasHeight = 250;
  signaturePad!: SignaturePad;

  constructor(private formBuilder: FormBuilder, private messageService: MessageService) {
    this.Form = this.formBuilder.group({
      signature: [''],
      name: ['', [Validators.required]],
      job_position: ['', [Validators.required]],
      comments: ['']
    });
  }

  ngOnInit(): void {
    this.quoteId = this._route.snapshot.paramMap.get('id');
  }

  ngAfterViewInit(): void {
    this.signaturePad = new SignaturePad(this.digitalSign.nativeElement);
    this.CanvasWidth = this.wrapper.nativeElement.offsetWidth;
  }

  onResize(): void {
    this.CanvasWidth = this.wrapper.nativeElement.offsetWidth;
  }

  accept(): void {
    if (!this.quoteId || this.signaturePad.isEmpty()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Firma requerida',
        detail: 'Dibuja tu firma antes de aprobar la cotización.'
      });
      return;
    }

    const signature = this.dataURLtoFile(this.signaturePad.toDataURL(), 'firma.jpg');
    this.Form.get('signature')?.setValue(signature);

    if (this.Form.invalid) {
      return;
    }

    this._quotesService.approveQuote(this.quoteId, {
      signature,
      name: this.Form.get('name')?.value,
      job_position: this.Form.get('job_position')?.value,
      comments: this.Form.get('comments')?.value ?? ''
    }).subscribe({
      next: () => {
        this.quoteApproved = true;
        this.messageService.add({
          severity: 'success',
          summary: 'Cotización aceptada',
          detail: 'La cotización ha sido aceptada exitosamente.'
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error al aprobar la cotización',
          detail: 'Ha ocurrido un error al aprobar la cotización, por favor intente nuevamente.'
        });
      }
    });
  }

  cancel(): void {
    this.Form.reset();
    this.signaturePad.clear();
  }

  clear(): void {
    this.signaturePad.clear();
    this.Form.get('signature')?.setValue('');
  }

  dataURLtoFile(dataUrl: string, filename: string): File {
    const [metadata, data] = dataUrl.split(',');
    const mime = metadata.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
    const binaryString = atob(data);
    const bytes = new Uint8Array(binaryString.length);

    for (let index = 0; index < binaryString.length; index++) {
      bytes[index] = binaryString.charCodeAt(index);
    }

    return new File([bytes], filename, { type: mime });
  }
}
